import { useState, useEffect } from 'react'
import { emailTemplatesApi } from '../services/api'
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  FormField,
  FormTextarea
} from './common'

const TONE_PRESETS = ['Professional', 'Friendly', 'Formal', 'Playful', 'Technical', 'Luxury']
const STYLE_PRESETS = ['Minimal', 'Bold', 'Corporate', 'Warm', 'Dark']
const POLISH_PRESETS = ['Tighten spacing', 'Better CTA', 'Modernize', 'More branded', 'Simplify']
const VARIATION_HINTS = [
  'Bold hero banner with large imagery and strong typography',
  'Minimalist single-column with generous whitespace and subtle dividers',
  'Card-based layout with distinct content blocks and rounded corners',
  'Magazine-style multi-column layout with sidebar elements',
  'Gradient header section with layered depth and shadow effects',
  'Centered single-column with elegant serif typography and thin borders'
]

const BrandingTab = () => {
  const [config, setConfig] = useState({
    companyName: 'Reziland',
    logoUrl: '',
    primaryColor: '#ff6f0f',
    secondaryColor: '#2B0B98',
    styleDescription: '',
    referenceTemplateHtml: '',
    tonePreset: ''
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [previewMode, setPreviewMode] = useState('preview')
  const [polishPrompt, setPolishPrompt] = useState('')
  const [candidates, setCandidates] = useState([])
  const [candidateIndex, setCandidateIndex] = useState(0)
  const [variationCount, setVariationCount] = useState(4)
  const [slotStatuses, setSlotStatuses] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      setIsLoading(true)
      const response = await emailTemplatesApi.getBrandingConfig()
      if (response.data) {
        setConfig({
          companyName: response.data.companyName || 'Reziland',
          logoUrl: response.data.logoUrl || '',
          primaryColor: response.data.primaryColor || '#ff6f0f',
          secondaryColor: response.data.secondaryColor || '#2B0B98',
          styleDescription: response.data.styleDescription || '',
          referenceTemplateHtml: response.data.referenceTemplateHtml || '',
          tonePreset: response.data.tonePreset || ''
        })
      }
    } catch (err) {
      const status = err.response?.status
      if (status === 500) {
        setError('Branding config not available — the database migration may need to be applied')
      } else {
        setError('Failed to load branding config')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      await emailTemplatesApi.updateBrandingConfig(config)
      setSuccess('Branding config saved successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data || 'Failed to save branding config')
    } finally {
      setIsSaving(false)
    }
  }

  const handleGenerateReference = async (preset) => {
    setIsGenerating(true)
    setCandidates([])
    setCandidateIndex(0)
    setError(null)

    const statuses = Array(variationCount).fill('generating')
    setSlotStatuses(statuses)

    const promises = Array.from({ length: variationCount }, (_, i) =>
      emailTemplatesApi.generateReferenceHtml({
        companyName: config.companyName,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        logoUrl: config.logoUrl || undefined,
        tonePreset: config.tonePreset || undefined,
        styleDescription: config.styleDescription || undefined,
        stylePreset: preset || undefined,
        variationHint: variationCount > 1 ? VARIATION_HINTS[i % VARIATION_HINTS.length] : undefined
      }).then(res => {
        const html = res.data?.html
        if (html) {
          setSlotStatuses(prev => {
            const next = [...prev]
            next[i] = 'done'
            return next
          })
          setCandidates(prev => [...prev, html])
        } else {
          setSlotStatuses(prev => {
            const next = [...prev]
            next[i] = 'error'
            return next
          })
        }
      }).catch(() => {
        setSlotStatuses(prev => {
          const next = [...prev]
          next[i] = 'error'
          return next
        })
      })
    )

    await Promise.allSettled(promises)
    setIsGenerating(false)
  }

  const handleGenerateFromStyle = () => handleGenerateReference(null)

  const handlePolish = async () => {
    if (!config.referenceTemplateHtml) return
    setIsGenerating(true)
    setError(null)

    try {
      const res = await emailTemplatesApi.generateReferenceHtml({
        companyName: config.companyName,
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
        logoUrl: config.logoUrl || undefined,
        tonePreset: config.tonePreset || undefined,
        styleDescription: config.styleDescription || undefined,
        currentHtml: config.referenceTemplateHtml,
        polishPrompt: polishPrompt || undefined
      })

      if (res.data?.html) {
        setCandidates([res.data.html])
        setCandidateIndex(0)
      }
    } catch (err) {
      setError('Failed to polish template')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleAcceptCandidate = async () => {
    const html = candidates[candidateIndex]
    if (!html) return

    const updatedConfig = { ...config, referenceTemplateHtml: html }
    setConfig(updatedConfig)
    setCandidates([])

    try {
      setIsSaving(true)
      await emailTemplatesApi.updateBrandingConfig(updatedConfig)
      setSuccess('Reference template saved')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError('Failed to save reference template')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) return <LoadingSpinner />

  return (
    <div className="flex gap-6" style={{ minHeight: '600px' }}>
      {/* Left Panel - Settings */}
      <div className="space-y-6" style={{ width: '380px', flexShrink: 0 }}>
        {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
        {success && <SuccessMessage message={success} onDismiss={() => setSuccess(null)} />}

        {/* Brand Identity */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">Brand Identity</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <FormField
                label="Company Name"
                value={config.companyName}
                onChange={(e) => setConfig(c => ({ ...c, companyName: e.target.value }))}
                maxLength={100}
              />
              <FormField
                label="Logo URL"
                value={config.logoUrl}
                onChange={(e) => setConfig(c => ({ ...c, logoUrl: e.target.value }))}
                placeholder="https://..."
                maxLength={500}
              />
              {config.logoUrl && (
                <div className="p-2 border border-gray-200 rounded-lg bg-gray-50 flex justify-center">
                  <img
                    src={config.logoUrl}
                    alt="Logo preview"
                    className="max-h-12 object-contain"
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config.primaryColor}
                      onChange={(e) => setConfig(c => ({ ...c, primaryColor: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={config.primaryColor}
                      onChange={(e) => setConfig(c => ({ ...c, primaryColor: e.target.value }))}
                      className="flex-1 input-field text-sm"
                      maxLength={20}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config.secondaryColor}
                      onChange={(e) => setConfig(c => ({ ...c, secondaryColor: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                    />
                    <input
                      type="text"
                      value={config.secondaryColor}
                      onChange={(e) => setConfig(c => ({ ...c, secondaryColor: e.target.value }))}
                      className="flex-1 input-field text-sm"
                      maxLength={20}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Style & Tone */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">Style & Tone</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tone Presets</label>
                <div className="flex flex-wrap gap-2">
                  {TONE_PRESETS.map(tone => (
                    <button
                      key={tone}
                      type="button"
                      onClick={() => {
                        setConfig(c => ({
                          ...c,
                          tonePreset: tone,
                          styleDescription: c.styleDescription
                            ? `${c.styleDescription}, ${tone.toLowerCase()}`
                            : tone.toLowerCase()
                        }))
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                        config.tonePreset === tone
                          ? 'bg-primary-100 text-primary-700 border-primary-300'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {tone}
                    </button>
                  ))}
                </div>
              </div>
              <FormTextarea
                label="Style Description"
                value={config.styleDescription}
                onChange={(e) => setConfig(c => ({ ...c, styleDescription: e.target.value }))}
                rows={5}
                maxLength={2000}
                placeholder="Describe the visual style for generated email templates..."
              />
              <Button variant="primary" onClick={handleSave} loading={isSaving} className="w-full">
                Save Branding
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Generate New Reference */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">Generate New Reference</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Create a fresh reference template from a visual style preset or your saved style description.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Variations</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setVariationCount(n)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium border transition-colors ${
                        variationCount === n
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-primary-300'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Style Presets</label>
                <div className="flex flex-wrap gap-2">
                  {STYLE_PRESETS.map(preset => (
                    <button
                      key={preset}
                      type="button"
                      disabled={isGenerating}
                      onClick={() => handleGenerateReference(preset)}
                      className="px-3 py-1.5 text-xs font-medium rounded-full border bg-gray-50 text-gray-600 border-gray-200 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300 disabled:opacity-50 transition-colors"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                variant="secondary"
                onClick={handleGenerateFromStyle}
                disabled={isGenerating || !config.styleDescription}
                loading={isGenerating}
                className="w-full"
              >
                From Style Description
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Polish Current Template */}
        {config.referenceTemplateHtml && (
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Polish Current Template</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Refine the current reference template while keeping its structure.
                </p>
                <div className="flex flex-wrap gap-2">
                  {POLISH_PRESETS.map(preset => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setPolishPrompt(preset)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                        polishPrompt === preset
                          ? 'bg-primary-100 text-primary-700 border-primary-300'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
                <FormTextarea
                  label="Custom Polish Instruction"
                  value={polishPrompt}
                  onChange={(e) => setPolishPrompt(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Describe how to improve the current template..."
                />
                <Button
                  variant="primary"
                  onClick={handlePolish}
                  disabled={isGenerating || !polishPrompt}
                  loading={isGenerating}
                  className="w-full"
                >
                  Polish
                </Button>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Right Panel - Preview */}
      <div className="flex-1 sticky top-0" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <Card className="h-full flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {!isGenerating && candidates.length > 0
                  ? 'Choose a Variation'
                  : isGenerating && !config.referenceTemplateHtml
                    ? 'Generating Variations...'
                    : 'Reference Preview'}
              </h3>
              {(isGenerating || candidates.length === 0) && config.referenceTemplateHtml && (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setPreviewMode('preview')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      previewMode === 'preview'
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('code')}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      previewMode === 'code'
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    HTML
                  </button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardBody className="flex-1 overflow-hidden flex flex-col">
            {/* Progress Bar — shown during generation, above the preview */}
            {isGenerating && slotStatuses.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-3 mb-2 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex gap-2">
                  {slotStatuses.map((status, i) => (
                    <div key={i} className="flex items-center gap-1 px-2 py-1 rounded bg-white border border-gray-200">
                      {status === 'generating' && (
                        <div className="w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      )}
                      {status === 'done' && <span className="text-green-500 text-sm">&#10003;</span>}
                      {status === 'error' && <span className="text-red-500 text-sm">&#10005;</span>}
                      <span className="text-xs text-gray-500">{i + 1}</span>
                    </div>
                  ))}
                </div>
                <span className="text-sm text-gray-500">
                  {slotStatuses.filter(s => s !== 'generating').length} of {slotStatuses.length} complete
                </span>
              </div>
            )}

            {/* Carousel View — shown only after generation finishes with results */}
            {!isGenerating && candidates.length > 0 && (
              <div className="flex flex-col h-full">
                {candidates.length > 1 && (
                  <div className="flex items-center justify-between mb-3">
                    <button
                      type="button"
                      onClick={() => setCandidateIndex(i => Math.max(0, i - 1))}
                      disabled={candidateIndex === 0}
                      className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-30"
                    >
                      &larr; Prev
                    </button>
                    <span className="text-sm text-gray-600">
                      {candidateIndex + 1} of {candidates.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCandidateIndex(i => Math.min(candidates.length - 1, i + 1))}
                      disabled={candidateIndex >= candidates.length - 1}
                      className="px-3 py-1.5 text-sm bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-30"
                    >
                      Next &rarr;
                    </button>
                  </div>
                )}
                <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={candidates[candidateIndex]}
                    className="w-full h-full"
                    title="Candidate Preview"
                    sandbox="allow-same-origin allow-scripts"
                    style={{ minHeight: '400px' }}
                  />
                </div>
                <div className="flex justify-end gap-3 mt-3">
                  <Button variant="secondary" onClick={() => { setCandidates([]); setCandidateIndex(0) }}>Cancel</Button>
                  <Button variant="primary" onClick={handleAcceptCandidate} loading={isSaving}>
                    Use This Template
                  </Button>
                </div>
              </div>
            )}

            {/* Single Preview — visible during generation (with progress above) and when idle */}
            {(isGenerating || candidates.length === 0) && config.referenceTemplateHtml && (
              <div className="flex-1 overflow-hidden">
                {previewMode === 'preview' ? (
                  <iframe
                    srcDoc={config.referenceTemplateHtml}
                    className="w-full h-full border border-gray-200 rounded-lg"
                    title="Reference Preview"
                    sandbox="allow-same-origin allow-scripts"
                    style={{ minHeight: '400px' }}
                  />
                ) : (
                  <textarea
                    value={config.referenceTemplateHtml}
                    onChange={(e) => setConfig(c => ({ ...c, referenceTemplateHtml: e.target.value }))}
                    className="w-full h-full font-mono text-xs p-3 border border-gray-200 rounded-lg resize-none"
                    style={{ minHeight: '400px' }}
                  />
                )}
              </div>
            )}

            {/* Empty State */}
            {!isGenerating && candidates.length === 0 && !config.referenceTemplateHtml && (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <p className="text-lg mb-2">No reference template yet</p>
                  <p className="text-sm">Use the style presets or your style description to generate one</p>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default BrandingTab
